import { useFeatureFlag } from '@agentnet/feature-flags';
import { AutocompleteMultiSelect, MultiSelectOption } from '@fluentsms/agentnet-web-components';
import { Box, FormControlLabel, Switch } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { AgentNetCheckBox } from 'ui-kit/inputs';
import AgentNetCheckBoxGroup from 'ui-kit/inputs/CheckBoxGroup/CheckBoxGroup';
import FormDrawerComponent from '../drawer/FormDrawerComponent';
import RadioGroup from '../radios/RadioGroup';

interface OrderInsightsEmailPreferenceDrawerProps {
  onClose: () => void;
  open: boolean;
  onUpdateEmailPreferences: (value: OrderInsightsEmailPreference) => Promise<void>;
  drawerData: OrderInsightsEmailPreference;
  isActionInProgress?: boolean;
}

type EmailFor = {
  onlyMyFiles: boolean;
  offices: number[];
};

type OfficeMultiSelectOption = MultiSelectOption<'id', 'name'>;

type NotificationTypes = {
  insightsUnavailable: boolean;
  watchListProperty: boolean;
  watchListParty: boolean;
  claimProperty: boolean;
  claimParty: boolean;
  vacant: boolean;
  ownerName: boolean;
  foreign: boolean;
  nonOwner: boolean;
  ownerAddr: boolean;
  lowMarket: boolean;
  lowAssess: boolean;
  finCEN: boolean;
  authLimit: boolean;
  sdn: boolean;
  identityVerificationStatus: boolean;
};

export type OrderInsightsEmailPreference = {
  firmId: string;
  isEnabled: boolean;
  emailFor?: EmailFor;
  notificationTypes?: NotificationTypes;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    alertContainer: {
      marginBottom: '62px',
    },
    alertMsg: {
      width: '-webkit-fill-available !important',
      maxWidth: 'unset !important',
      position: 'absolute',
      bottom: '49px',
      left: 0,
      padding: '6px 24px',
      zIndex: 3,
      borderTop: '1px solid #c4c4c4',
      borderRadius: 0,
    },
    checkboxGrid: {
      minWidth: '100%',
      '& .MuiFormGroup-root': {
        display: 'grid !important',
        gridTemplateColumns: '1fr 1fr !important',
        columnGap: `${theme.spacing(3)}px !important`,
        rowGap: `${theme.spacing(1)}px !important`,
      },
    },
  }),
);

const OrderInsightsEmailPreferenceDrawer = ({
  onClose,
  open,
  onUpdateEmailPreferences,
  drawerData,
}: OrderInsightsEmailPreferenceDrawerProps) => {
  const classes = useStyles();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm } = profileCtx;
  const showFincenInOI = useFeatureFlag(FeatureFlag.SHOW_FINCEN_IN_ORDER_INSIGHTS, false);

  const emailForOptions = useMemo(
    () => [
      { name: 'MyFiles', value: 'MyFiles', dataTestId: 'orderinsights-email-myfiles', label: 'Only My Files' },
      { name: 'Offices', value: 'Offices', dataTestId: 'orderinsights-email-offices', label: 'Select Offices' },
    ],
    [],
  );

  const propertyAlertDefaults = useMemo(
    () => [
      {
        name: 'insightsUnavailable',
        value: false,
        label: 'Insights Unavailable: Address Match Not Found',
        dataTestId: 'insightsUnavailable',
      },
      { name: 'watchListProperty', value: false, label: 'Potential Watchlist', dataTestId: 'watchListProperty' },
      { name: 'claimProperty', value: false, label: 'Potential Claim', dataTestId: 'claimProperty' },
      { name: 'vacant', value: false, label: 'Potential Vacant Land', dataTestId: 'vacant' },
      { name: 'ownerName', value: false, label: 'Potential Owner Name Mismatches', dataTestId: 'ownerName' },
      { name: 'foreign', value: false, label: 'Potential Foreign Seller', dataTestId: 'foreign' },
      { name: 'ownerAddr', value: false, label: 'Potential Owner Address Mismatches', dataTestId: 'ownerAddr' },
      { name: 'nonOwner', value: false, label: 'Potential Non-Owner Occupied Property', dataTestId: 'nonOwner' },
      { name: 'lowMarket', value: false, label: 'Potential Low Market Value', dataTestId: 'lowMarket' },
      { name: 'lowAssess', value: false, label: 'Potential Low Assessed Value', dataTestId: 'lowAssess' },
      ...(showFincenInOI?.value
        ? [{ name: 'finCEN', value: false, label: 'Potential FinCEN RRE Match', dataTestId: 'finCEN' }]
        : []),
      { name: 'authLimit', value: false, label: 'Authorized Risk Limit Exceeded', dataTestId: 'authLimit' },
    ],
    [showFincenInOI],
  );

  const partyAlertDefaults = useMemo(
    () => [
      { name: 'sdn', value: false, label: 'Potential SDN Match', dataTestId: 'sDN' },
      { name: 'watchListParty', value: false, label: 'Potential Watchlist', dataTestId: 'watchListParty' },
      { name: 'claimParty', value: false, label: 'Potential Claim', dataTestId: 'claimParty' },
      {
        name: 'identityVerificationStatus',
        value: false,
        label: 'Identity Verification Request Status',
        dataTestId: 'identityVerification',
      },
    ],
    [],
  );

  const [isEnabled, setEnable] = useState<boolean>(drawerData?.isEnabled || false);
  const [saveClicked, setSaveClicked] = useState<boolean>(false);
  const [emailFor, setEmailFor] = useState<string>(drawerData?.emailFor?.onlyMyFiles == true ? 'MyFiles' : 'Offices');

  const getSelectedEmailFor = () => {
    if (drawerData?.emailFor?.onlyMyFiles) {
      return emailForOptions[0];
    } else {
      return emailForOptions[1];
    }
  };

  const [selectedEmailFor, setSelectedEmailFor] = useState(getSelectedEmailFor());
  const [offices, setOffices] = useState<OfficeMultiSelectOption[]>([]);
  const [selectedOffices, setSelectedOffices] = useState<OfficeMultiSelectOption[]>([]);
  const [propertyAlertOptions, setPropertyAlertOptions] = useState(propertyAlertDefaults);
  const [partyAlertOptions, setPartyAlertOptions] = useState(partyAlertDefaults);

  const emailForError = useMemo(
    () => emailFor == 'Offices' && selectedOffices.length == 0,
    [emailFor, selectedOffices],
  );
  const notificationTypeError = useMemo(
    () => propertyAlertOptions.every((option) => !option.value) && partyAlertOptions.every((option) => !option.value),
    [propertyAlertOptions, partyAlertOptions],
  );

  const initializePreferences = () => {
    if (drawerData.emailFor?.onlyMyFiles) {
      setEmailFor('MyFiles');
      setSelectedEmailFor(emailForOptions[0]);
    } else {
      setEmailFor('Offices');
      setSelectedEmailFor(emailForOptions[1]);
    }
    const firmOffices = userFirm?.offices?.filter((ele: any) => ele.status == 'Active');
    const officesToSet = firmOffices?.map((office: any) => ({
      id: office.officeId,
      name: office.name,
    }));

    setOffices(officesToSet || []);

    if (!drawerData.emailFor?.onlyMyFiles) {
      const selectedOffices = drawerData.emailFor?.offices
        .map((id) => officesToSet?.find((office: OfficeMultiSelectOption) => office.id == id.toString()))
        .filter((office) => office !== null);

      if ((selectedOffices ?? []).length > 0) {
        setSelectedOffices(selectedOffices as OfficeMultiSelectOption[]);
      } else {
        setSelectedOffices(officesToSet as OfficeMultiSelectOption[]);
      }
    }

    setPropertyAlertOptions(
      propertyAlertDefaults.map((option) => ({
        ...option,
        value: drawerData.notificationTypes?.[option.name as keyof NotificationTypes] ?? false,
      })),
    );
    setPartyAlertOptions(
      partyAlertDefaults.map((option) => ({
        ...option,
        value: drawerData.notificationTypes?.[option.name as keyof NotificationTypes] ?? true,
      })),
    );
  };

  useEffect(() => {
    if (!open) {
      handleCloseDrawer();
      return;
    }
    setSaveClicked(false);
    if (drawerData?.isEnabled) {
      setEnable(true);
      initializePreferences();
      if (drawerData?.emailFor?.onlyMyFiles) {
        setSelectedOffices([]);
      }
    } else {
      setEnable(false);
    }
  }, [open]);

  useEffect(() => {
    if (isEnabled && !drawerData?.isEnabled) {
      initializePreferences();
    }
  }, [isEnabled]);

  useEffect(() => {
    if (emailFor == 'MyFiles') {
      setSelectedEmailFor(emailForOptions[0]);
    } else {
      setSelectedEmailFor(emailForOptions[1]);
      if ((selectedOffices ?? []).length > 0) {
        setSelectedOffices(selectedOffices as OfficeMultiSelectOption[]);
      } else {
        setSelectedOffices(offices as OfficeMultiSelectOption[]);
      }
    }
  }, [emailFor]);

  const handleEnable = () => {
    setEnable(!isEnabled);
  };

  const handleEmailForChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailFor((event.target as HTMLInputElement).value);
  };

  const createAlertChangeHandler =
    (setter: React.Dispatch<React.SetStateAction<typeof propertyAlertDefaults>>, selectAllName: string) =>
    (name: string, checked: boolean) => {
      setter((prev) =>
        name === selectAllName
          ? prev.map((option) => ({ ...option, value: checked }))
          : prev.map((option) => (option.name === name ? { ...option, value: checked } : option)),
      );
    };

  const handlePropertyAlertChange = useCallback(
    createAlertChangeHandler(setPropertyAlertOptions, 'PropertySelectAll'),
    [],
  );
  const handlePartyAlertChange = useCallback(createAlertChangeHandler(setPartyAlertOptions, 'PartySelectAll'), []);

  const handleSave = () => {
    setSaveClicked(true);
    if (isEnabled && (notificationTypeError || emailForError)) {
      return; // Exit early if validation fails
    }

    const preferences: OrderInsightsEmailPreference = {
      firmId: drawerData.firmId,
      isEnabled,
      ...(isEnabled && {
        emailFor: {
          onlyMyFiles: emailFor === 'MyFiles',
          offices: emailFor === 'Offices' ? selectedOffices.map((office) => parseInt(office.id, 10)) : [],
        },
        notificationTypes: [...propertyAlertOptions, ...partyAlertOptions].reduce((acc, option) => {
          acc[option.name as keyof NotificationTypes] = option.value;
          return acc;
        }, {} as NotificationTypes),
      }),
    };

    resetDrawer();
    onUpdateEmailPreferences(preferences);
    handleCloseDrawer();
  };

  const resetDrawer = () => {
    setEmailFor('MyFiles');
    setSelectedOffices([]);
    setSelectedEmailFor(emailForOptions[0]);
  };

  const renderAlertSection = (
    title: string,
    options: typeof propertyAlertDefaults,
    selectAllName: string,
    onChange: (name: string, checked: boolean) => void,
  ) => {
    const allChecked = options.every((o) => o.value);
    const someChecked = options.some((o) => o.value);
    const testIdPrefix = title.toLowerCase().replace(/\s+/g, '-');

    return (
      <div>
        <Typography variant="h6">{title}</Typography>
        <Box mt={1} mb={2}>
          <AgentNetCheckBoxGroup layout="row" required removeMarginTop={false}>
            <AgentNetCheckBox
              label="Select All"
              name={selectAllName}
              value={allChecked}
              indeterminate={someChecked && !allChecked}
              checkHandler={(checked: boolean, name: string) => onChange(name, checked)}
              data-testid={`order-insights-email-${testIdPrefix}-select-all`}
            />
          </AgentNetCheckBoxGroup>
        </Box>
        <AgentNetCheckBoxGroup layout="twoColumn" required removeMarginTop={false} className={classes.checkboxGrid}>
          {options.map((option, index) => (
            <AgentNetCheckBox
              key={index}
              label={option.label}
              name={option.name}
              value={option.value}
              checkHandler={(checked: boolean, name: string) => onChange(name, checked)}
              data-testid={option.dataTestId}
            />
          ))}
        </AgentNetCheckBoxGroup>
      </div>
    );
  };

  const renderMessage = useCallback(
    (msg: string) => (
      <>
        <div className={classes.alertContainer}>
          <Alert
            className={classes.alertMsg}
            severity="error"
            data-testid="order-insights-email-preference-error-message"
          >
            {msg}
          </Alert>
        </div>
      </>
    ),
    [],
  );

  const errorMessages = () => {
    if (emailForError && notificationTypeError) {
      return renderMessage('Please select at least one office and one notification type to save email preferences.');
    } else if (emailForError) {
      return renderMessage('Please select at least one office to save email preferences');
    } else if (notificationTypeError) {
      return renderMessage('Please select at least one notification type.');
    }
  };

  const handleCloseDrawer = () => {
    resetDrawer();
    onClose();
  };

  return (
    <FormDrawerComponent
      title="Email Preferences"
      open={open}
      primaryActionLabel={'Save'}
      onPrimaryAction={handleSave}
      onDismissAction={handleCloseDrawer}
    >
      <Box mb={1}>
        <Typography variant="body1" color="textSecondary">
          Manage your email preferences for Order Insights to stay updated on all potential issues that matter to you.
        </Typography>
      </Box>
      <Box mb={1}>
        <FormControlLabel
          control={<Switch checked={isEnabled} onChange={handleEnable} name="enabled" />}
          label="Enable Email Notifications"
        />
      </Box>
      {isEnabled && (
        <div>
          <Box mb={2}>
            <Box mb={2}>
              <RadioGroup
                options={emailForOptions}
                onChange={handleEmailForChange}
                selected={selectedEmailFor}
                label="Receive emails for:"
                data-testid="order-insights-receive-email-for"
              />
            </Box>
            <div>
              <AutocompleteMultiSelect
                label="Offices"
                options={offices}
                keyField="id"
                labelField="name"
                value={selectedOffices}
                onChange={(value) => {
                  const officeOptions = value.map((option) => ({
                    id: option.id,
                    name: option.name,
                  }));
                  setSelectedOffices(officeOptions);
                }}
                required={emailFor == 'Offices'}
                disabled={emailFor != 'Offices' || offices?.length == 0}
                testId="order-insights-email-offices-dropdown"
              />
            </div>
          </Box>
          <Box mt={2} mb={2}>
            <Typography variant="h4">Notification Types</Typography>
          </Box>
          {renderAlertSection('Property Alerts', propertyAlertOptions, 'PropertySelectAll', handlePropertyAlertChange)}
          <Box mt={2}>
            {renderAlertSection('Party Alerts', partyAlertOptions, 'PartySelectAll', handlePartyAlertChange)}
          </Box>
          {saveClicked && errorMessages()}
        </div>
      )}
    </FormDrawerComponent>
  );
};

export default OrderInsightsEmailPreferenceDrawer;
